import UploadIcon from "@mui/icons-material/Upload";
import {
  Backdrop,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useContext, useRef, useState } from "react";
import "../../../../Tooltip/styles/tooltip.css";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import { useEmailAdminUser } from "../FileQueries/emailAdminUser";
import { useUploadFiles } from "../FileQueries/uploadFileQueries";
import "./styles/create-file-popover.css";

// Create a new file popover
const CreateFilePopover = ({
  open,
  setOpen,
  projectId,
  queryClient,
  userType,
  folder,
}) => {
  const { clientId } = useContext(DashboardContext);
  const [newFileState, setNewFileState] = useState({
    fileName: "",
    file: null,
    amended: false,
  });
  const newFileRef = useRef(null);
  const existingFileRef = useRef(null);
  const [showNameInput, setShowNameInput] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");
  const [openBackdrop, setOpenBackdrop] = useState(false);

  const uploadFiles = useUploadFiles(projectId, queryClient);
  const { enqueueSnackbar } = useSnackbar();
  const emailAdmin = useEmailAdminUser(clientId, projectId, enqueueSnackbar);

  const { data: uploadedData, isFetched: uploadedDataFetched } = useQuery({
    queryKey: ["uploaded-project-files", projectId, folder, userType],
    queryFn: async () => {
      const res = await axios.get(
        `/api/uploaded-project-files/${projectId}/${folder}/${userType}/`,
        {
          withCredentials: true,
        },
      );
      return res.data;
    },
    enabled: !!projectId,
  });

  const handleUploadFile = async (files) => {
    try {
      setOpenBackdrop(true);
      await uploadFiles.mutateAsync({
        clientId,
        projectId,
        folder: folder,
        files: files,
        amended: newFileState.amended,
      });

      enqueueSnackbar(
        `${files.length > 1 ? "All files" : "File"} uploaded successfully`,
        {
          variant: "success",
        },
      );

      await emailAdmin.mutateAsync({
        folder: folder,
        files: files,
      });
    } catch (error) {
      enqueueSnackbar(
        `${files.length > 1 ? "All files" : "File"} failed to upload`,
        {
          variant: "error",
        },
      );
    } finally {
      setNewFileState({
        name: "",
        fileStatus: "AT",
        file: null,
      });
      setSelectedFile("");
      setOpen(false);
      setOpenBackdrop(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const uploadedFiles = Array.from(e.dataTransfer.files);
    if (uploadedFiles.length === 0) return;

    enqueueSnackbar(
      `Uploading ${uploadedFiles.length} file${
        uploadedFiles.length > 1 ? "s" : ""
      }`,
      {
        variant: "info",
        autoHideDuration: 1500,
      },
    );
    await handleUploadFile(uploadedFiles);
  };

  const handleFileSelect = async (e) => {
    const uploadedFilesList = Array.from(e.target.files);

    if (uploadedFilesList.length === 0) return;

    enqueueSnackbar(
      `Uploading ${uploadedFilesList.length} file${
        uploadedFilesList.length > 1 ? "s" : ""
      }`,
      {
        variant: "info",
        autoHideDuration: 1500,
      },
    );

    await handleUploadFile(uploadedFilesList);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClose = () => {
    setNewFileState({
      name: "",
      fileStatus: "AT",
      file: null,
    });
    setSelectedFile("");
    setShowNameInput(false);
    setOpen(false);
  };

  return (
    <Modal id="create-file-modal" open={open} onClose={handleClose}>
      <div
        id="create-file-form"
        style={
          showNameInput
            ? {
                height: "570px",
                maxHeight: "570px",
              }
            : {
                height: "500px",
                maxHeight: "500px",
              }
        }
      >
        <Backdrop id="create-file-backdrop" open={openBackdrop}>
          UPLOADING DO NOT LEAVE THIS PAGE
          <CircularProgress id="create-file-progress" />
        </Backdrop>
        <h1 id="create-file-heading">Upload New File</h1>
        <hr id="create-file-hr" />
        <ToggleButtonGroup
          value={newFileState.amended}
          exclusive
          onChange={(e, newValue) => {
            if (newValue !== null) {
              setNewFileState((prev) => ({
                ...prev,
                amended: newValue,
              }));
            }
          }}
          className="amendment-toggle-group"
          id="amendment-toggle-group"
          size="small"
        >
          <ToggleButton value={false} className="amendment-toggle">
            New File
          </ToggleButton>
          <ToggleButton value={true} className="amendment-toggle">
            Amendment
          </ToggleButton>
        </ToggleButtonGroup>
        <div id="drag-files-box-container">
          <div
            id="drag-files-box"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById("new-file-input").click()}
          >
            <UploadIcon id="upload-new-icon" className="upload-new-icon" />
            <span className="drag-files-text" id="drag-files-text">
              <u>Click to select</u> or Drag & Drop files here
            </span>
            <input
              type="file"
              id="new-file-input"
              hidden
              multiple
              key={selectedFile}
              onChange={handleFileSelect}
              ref={existingFileRef}
              // disabled={showNameInput}
            />
          </div>
        </div>
        <div id="new-files-btn-container">
          <Button
            className="cancel-new-file-btn"
            id="cancel-new-file-btn"
            variant="contained"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateFilePopover;
